/**
 * React Query hooks for cross-tenant access settings
 *
 * Provides data fetching and mutations for default policy and partner configurations
 * with automatic caching and cache invalidation.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CrossTenantAccessApiService } from '../services/crossTenantAccessApi';
import type {
  CrossTenantDefaultPolicy,
  CrossTenantDefaultPolicyUpdateRequest,
  CrossTenantPartner,
  CrossTenantPartnerCreateRequest,
  CrossTenantPartnerUpdateRequest,
} from '../types/crossTenantAccess';

/**
 * Query key factories for cross-tenant access
 */
export const crossTenantDefaultPolicyQueryKey = (tenantId: string) =>
  ['tenant', tenantId, 'crossTenantAccess', 'defaultPolicy'] as const;

export const crossTenantPartnersQueryKey = (tenantId: string) =>
  ['tenant', tenantId, 'crossTenantAccess', 'partners'] as const;

/**
 * Hook return type for useCrossTenantDefaultPolicy
 */
export interface UseCrossTenantDefaultPolicyResult {
  policy: CrossTenantDefaultPolicy | undefined;
  isLoading: boolean;
  error: Error | null;
  updatePolicy: (update: CrossTenantDefaultPolicyUpdateRequest) => Promise<void>;
  isUpdating: boolean;
  updateError: Error | null;
  refetch: () => void;
}

/**
 * React Query hook for managing default cross-tenant access policy
 *
 * @param tenantId - Tenant ID
 * @returns Default policy data, loading states, and mutation functions
 */
export function useCrossTenantDefaultPolicy(
  tenantId: string
): UseCrossTenantDefaultPolicyResult {
  const queryClient = useQueryClient();

  const {
    data: policy,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: crossTenantDefaultPolicyQueryKey(tenantId),
    queryFn: () => CrossTenantAccessApiService.getDefaultPolicy(tenantId),
  });

  const {
    mutateAsync: updatePolicy,
    isPending: isUpdating,
    error: updateError,
  } = useMutation({
    mutationFn: (update: CrossTenantDefaultPolicyUpdateRequest) =>
      CrossTenantAccessApiService.updateDefaultPolicy(tenantId, update),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: crossTenantDefaultPolicyQueryKey(tenantId),
      });
    },
  });

  return {
    policy,
    isLoading,
    error: error as Error | null,
    updatePolicy,
    isUpdating,
    updateError: updateError as Error | null,
    refetch,
  };
}

/**
 * Update partner request with partnerTenantId included
 */
export interface UpdatePartnerRequest extends CrossTenantPartnerUpdateRequest {
  partnerTenantId: string;
}

/**
 * Hook return type for useCrossTenantPartners
 */
export interface UseCrossTenantPartnersResult {
  partners: CrossTenantPartner[] | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  createPartner: (request: CrossTenantPartnerCreateRequest) => Promise<CrossTenantPartner>;
  isCreating: boolean;
  createError: Error | null;
  updatePartner: (request: UpdatePartnerRequest) => Promise<void>;
  isUpdating: boolean;
  updateError: Error | null;
  deletePartner: (partnerTenantId: string) => Promise<void>;
  isDeleting: boolean;
  deleteError: Error | null;
  refetch: () => void;
}

/**
 * React Query hook for managing cross-tenant partner configurations
 *
 * @param tenantId - Tenant ID
 * @returns Partner list, loading states, and mutation functions
 */
export function useCrossTenantPartners(
  tenantId: string
): UseCrossTenantPartnersResult {
  const queryClient = useQueryClient();

  const {
    data: partners,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: crossTenantPartnersQueryKey(tenantId),
    queryFn: () => CrossTenantAccessApiService.listPartners(tenantId),
  });

  const {
    mutateAsync: createPartner,
    isPending: isCreating,
    error: createError,
  } = useMutation({
    mutationFn: (request: CrossTenantPartnerCreateRequest) =>
      CrossTenantAccessApiService.createPartner(tenantId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: crossTenantPartnersQueryKey(tenantId),
      });
    },
  });

  const {
    mutateAsync: updatePartner,
    isPending: isUpdating,
    error: updateError,
  } = useMutation({
    mutationFn: (request: UpdatePartnerRequest) =>
      CrossTenantAccessApiService.updatePartner(tenantId, request.partnerTenantId, {
        inboundTrust: request.inboundTrust,
        b2bCollaboration: request.b2bCollaboration,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: crossTenantPartnersQueryKey(tenantId),
      });
    },
  });

  const {
    mutateAsync: deletePartner,
    isPending: isDeleting,
    error: deleteError,
  } = useMutation({
    mutationFn: (partnerTenantId: string) =>
      CrossTenantAccessApiService.deletePartner(tenantId, partnerTenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: crossTenantPartnersQueryKey(tenantId),
      });
    },
  });

  return {
    partners,
    isLoading,
    isFetching,
    error: error as Error | null,
    createPartner,
    isCreating,
    createError: createError as Error | null,
    updatePartner,
    isUpdating,
    updateError: updateError as Error | null,
    deletePartner,
    isDeleting,
    deleteError: deleteError as Error | null,
    refetch,
  };
}
